import React, { useState } from "react";
import { X } from "lucide-react";
import { HOME_THEME as T } from "../../lib/homeTheme";
import type { SongEntry } from "../../hooks/useSongLibrary";

const C = { bgElevated: "#1a1a1f" };

interface BordonaImportLibraryModalProps {
  open: boolean;
  songs: SongEntry[];
  onClose: () => void;
  onImport: (id: string) => Promise<void>;
  importingId?: string | null;
}

export const BordonaImportLibraryModal: React.FC<BordonaImportLibraryModalProps> = ({
  open,
  songs,
  onClose,
  onImport,
  importingId = null,
}) => {
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleImport = async (id: string) => {
    setError(null);
    try {
      await onImport(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo importar");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden border border-white/10 max-h-[85vh] flex flex-col"
        style={{ backgroundColor: C.bgElevated }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <h2 className="text-lg font-serif text-white/90">Importar a Bordona</h2>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/5">
            <X className="w-4 h-4 text-white/50" />
          </button>
        </div>

        <div className="p-6 flex flex-col min-h-0 flex-1">
          <p className="text-sm text-white/50 font-sans mb-4 shrink-0">
            Partituras de tu cuenta Edusico que subiste en Play u otra app. Elige solo las que quieras
            en guitarra.
          </p>

          {songs.length === 0 ? (
            <p className="text-sm text-white/40 text-center py-8">Nada pendiente de importar.</p>
          ) : (
            <ul className="flex-1 min-h-0 overflow-y-auto space-y-2">
              {songs.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-3 rounded-lg px-4 py-3 border border-white/10"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-white/80 font-sans truncate">{s.title}</p>
                    {s.artist && <p className="text-xs text-white/40 truncate">{s.artist}</p>}
                    {s.createdInApp === "play" && (
                      <p className="text-[10px] text-[#c4b5fd]/80 mt-0.5 uppercase tracking-wide">Desde Play</p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={importingId === s.id}
                    onClick={() => handleImport(s.id)}
                    className="shrink-0 px-3 py-1.5 rounded-md text-xs font-sans bg-[#c4b5fd]/20 text-[#c4b5fd] hover:bg-[#c4b5fd]/30 disabled:opacity-50"
                  >
                    {importingId === s.id ? "…" : "Importar"}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {error && <p className="text-sm text-red-400 mt-3">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default BordonaImportLibraryModal;
